import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reasons_for_using: [],
    selected_values: [],
    intention_statement: '',
    // Assessment data - now arrays for multi-select
    struggle_duration: [],
    daily_impact: 5,
    tried_before: [],
    support_system: ''
  });

  const trackOnboardingStep = async (stepNumber) => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;

      await fetch(`${BACKEND_URL}/api/profile/onboarding-step`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ step: stepNumber })
      });
    } catch (error) {
      console.error('Failed to track onboarding step:', error);
      // Don't block user experience if tracking fails
    }
  };

  const handleStepChange = (newStep) => {
    setStep(newStep);
    trackOnboardingStep(newStep);
  };

  // Track step 1 when component mounts
  useEffect(() => {
    trackOnboardingStep(1);
  }, []);

  const reasonsOptions = [
    'I want to break free from compulsive patterns',
    'I want to feel more in control of my choices',
    'I want better relationships and intimacy',
    'I want more energy and mental clarity',
    'I want to align my actions with my values',
    'I want to understand why I do what I do'
  ];

  const valuesOptions = [
    'Relationships',
    'Health',
    'Freedom',
    'Integrity',
    'Growth',
    'Connection',
    'Purpose',
    'Peace'
  ];

  const durationOptions = [
    { value: 'less_than_year', label: 'Less than a year' },
    { value: '1_3_years', label: '1-3 years' },
    { value: '3_5_years', label: '3-5 years' },
    { value: 'more_than_5', label: 'More than 5 years' }
  ];

  const triedBeforeOptions = [
    { value: 'first_time', label: 'This is my first time trying' },
    { value: 'tried_alone', label: "I've tried on my own before" },
    { value: 'tried_apps', label: "I've used other apps/programs" },
    { value: 'tried_therapy', label: "I've worked with a therapist" }
  ];

  const supportOptions = [
    { value: 'no_one', label: 'No one knows about my struggle' },
    { value: 'partner', label: 'My partner knows' },
    { value: 'friend_family', label: 'A friend or family member knows' },
    { value: 'therapist', label: 'I work with a therapist' },
    { value: 'group', label: "I'm in a support group" }
  ];

  const handleReasonToggle = (reason) => {
    setFormData(prev => ({
      ...prev,
      reasons_for_using: prev.reasons_for_using.includes(reason)
        ? prev.reasons_for_using.filter(r => r !== reason)
        : [...prev.reasons_for_using, reason]
    }));
  };

  const handleValueToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      selected_values: prev.selected_values.includes(value)
        ? prev.selected_values.filter(v => v !== value)
        : [...prev.selected_values, value]
    }));
  };

  const handleDurationToggle = (duration) => {
    setFormData(prev => ({
      ...prev,
      struggle_duration: prev.struggle_duration.includes(duration)
        ? prev.struggle_duration.filter(d => d !== duration)
        : [...prev.struggle_duration, duration]
    }));
  };

  const handleTriedBeforeToggle = (option) => {
    setFormData(prev => ({
      ...prev,
      tried_before: prev.tried_before.includes(option)
        ? prev.tried_before.filter(t => t !== option)
        : [...prev.tried_before, option]
    }));
  };

  const handleComplete = async () => {
    if (!formData.intention_statement.trim()) {
      toast.error('Please complete your intention statement');
      return;
    }

    setLoading(true);
    try {
      // Get token from localStorage
      const token = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${BACKEND_URL}/api/profile/onboarding`, {
        method: 'PUT',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          onboarding_completed: true
        })
      });

      if (!response.ok) throw new Error('Failed to save onboarding');

      toast.success('Welcome to your journey!');
      
      // Use window.location for reliable redirect after onboarding
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.message || 'Failed to complete onboarding');
      setLoading(false);
    }
  };

  const progressPercentage = (step / 5) * 100;

  return (
    <div className="min-h-screen bg-[#2D3640] flex flex-col">
      {/* Progress Bar */}
      <div className="w-full bg-[#3A4550] border-b border-[#4A5568]">
        <div 
          className="h-1 bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] transition-all duration-500 ease-out"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Step 1: Welcome */}
          {step === 1 && (
            <div className="space-y-8 text-center" data-testid="onboarding-welcome">
              <div className="space-y-4">
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                  Welcome to i am buddy
                </h1>
                <p className="text-lg text-[#CBD5E0] leading-relaxed max-w-sm mx-auto">
                  Your companion for recovery. A buddy who's with you every step of the journey.
                </p>
              </div>

              {/* Important Disclaimer */}
              <div className="bg-[#FFB627]/20 border-2 border-[#FFB627] rounded-xl p-5 text-left">
                <h3 className="text-sm font-bold text-[#FFB627] mb-2 flex items-center gap-2">
                  <span>⚠️</span>
                  <span>Before You Begin</span>
                </h3>
                <ul className="space-y-1.5 text-xs text-[#CBD5E0]">
                  <li>• This app is <strong className="text-white">peer support</strong>, not therapy or medical treatment</li>
                  <li>• We are <strong className="text-white">not a HIPAA-covered entity</strong></li>
                  <li>• If you're in crisis, call <strong className="text-[#FFB627]">988</strong> or <strong className="text-[#FFB627]">911</strong></li>
                  <li>• Continue working with your healthcare providers</li>
                  <li>• You must be <strong className="text-white">18 or older</strong> to use this app</li>
                </ul>
              </div>

              <div className="w-24 h-24 bg-[#00B4D8]/20 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-12 h-12 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>

              <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] p-5 text-left">
                <p className="text-sm text-[#CBD5E0] mb-3">Before we begin, we&apos;ll ask a few questions to personalize your experience. Everything you share is:</p>
                <ul className="space-y-2 text-sm text-[#CBD5E0]">
                  <li className="flex items-center gap-2">
                    <span className="text-[#06D6A0]">✓</span> Completely private
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#06D6A0]">✓</span> Used only to help you
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-[#06D6A0]">✓</span> Never shared or sold
                  </li>
                </ul>
              </div>

              <Button
                onClick={() => handleStepChange(2)}
                className="w-full rounded-full px-8 py-6 h-auto text-lg font-bold text-white bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8]"
                data-testid="onboarding-welcome-continue"
              >
                I Understand, Continue
              </Button>

              <p className="text-xs text-[#A0AEC0] text-center">
                By continuing, you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-[#00B4D8]">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#00B4D8]">Privacy Policy</a>
              </p>
            </div>
          )}

          {/* Step 2: Initial Assessment */}
          {step === 2 && (
            <div className="space-y-6" data-testid="onboarding-assessment">
              <div className="space-y-2 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold text-white">
                  A few questions about you
                </h2>
                <p className="text-sm text-[#CBD5E0]">
                  This helps us personalize your journey
                </p>
              </div>

              <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] p-6 space-y-6">
                {/* Duration */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-white">
                    How long have you been struggling with this?
                  </Label>
                  <p className="text-sm text-[#CBD5E0]">Select all that apply</p>
                  <div className="grid grid-cols-2 gap-3">
                    {durationOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleDurationToggle(option.value)}
                        className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${
                          formData.struggle_duration.includes(option.value)
                            ? 'border-[#00B4D8] bg-[#00B4D8]/20 text-[#00B4D8] ring-2 ring-[#00B4D8]/30'
                            : 'border-[#4A5568] bg-[#3A4550] hover:border-[#00B4D8]/50 text-[#CBD5E0]'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Daily Impact */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-white">
                    How much does this affect your daily life?
                  </Label>
                  <div className="space-y-2">
                    <Slider
                      value={[formData.daily_impact]}
                      onValueChange={(value) => setFormData({ ...formData, daily_impact: value[0] })}
                      min={1}
                      max={10}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-[#CBD5E0]">
                      <span>Minimal impact</span>
                      <span>Significant impact</span>
                    </div>
                  </div>
                </div>

                {/* Tried Before */}
                <div className="space-y-3">
                  <Label className="text-base font-medium text-white">
                    Have you tried to change this before?
                  </Label>
                  <p className="text-sm text-[#CBD5E0]">Select all that apply</p>
                  <div className="space-y-2">
                    {triedBeforeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleTriedBeforeToggle(option.value)}
                        className={`w-full p-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${
                          formData.tried_before.includes(option.value)
                            ? 'border-[#00B4D8] bg-[#00B4D8]/20 text-[#00B4D8] ring-2 ring-[#00B4D8]/30'
                            : 'border-[#4A5568] bg-[#3A4550] hover:border-[#00B4D8]/50 text-[#CBD5E0]'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleStepChange(1)}
                  variant="outline"
                  className="rounded-full px-6 py-5 h-auto text-base font-medium border-[#4A5568] text-[#CBD5E0] hover:bg-[#4A5568]"
                  data-testid="onboarding-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={() => handleStepChange(3)}
                  className="flex-1 rounded-full px-6 py-5 h-auto text-base font-bold text-white bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8]"
                  data-testid="onboarding-assessment-continue"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: What brings you here */}
          {step === 3 && (
            <div className="space-y-6" data-testid="onboarding-reasons">
              <div className="space-y-2 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold text-white">
                  What brings you here?
                </h2>
                <p className="text-sm text-[#CBD5E0]">
                  Select all that resonate with you
                </p>
              </div>

              <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] p-6 space-y-3">
                {reasonsOptions.map((reason) => (
                  <label
                    key={reason}
                    className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.reasons_for_using.includes(reason)
                        ? 'border-[#00B4D8] bg-[#00B4D8]/20 ring-2 ring-[#00B4D8]/30'
                        : 'border-[#4A5568] hover:border-[#00B4D8]/50 hover:bg-[#4A5568]'
                    }`}
                    data-testid={`reason-option-${reason}`}
                  >
                    <Checkbox
                      checked={formData.reasons_for_using.includes(reason)}
                      onCheckedChange={() => handleReasonToggle(reason)}
                      className="mt-0.5 border-[#CBD5E0] data-[state=checked]:bg-[#00B4D8] data-[state=checked]:border-[#00B4D8]"
                    />
                    <span className={`text-base leading-relaxed ${
                      formData.reasons_for_using.includes(reason) ? 'text-[#00B4D8]' : 'text-[#CBD5E0]'
                    }`}>
                      {reason}
                    </span>
                  </label>
                ))}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleStepChange(2)}
                  variant="outline"
                  className="rounded-full px-6 py-5 h-auto text-base font-medium border-[#4A5568] text-[#CBD5E0] hover:bg-[#4A5568]"
                  data-testid="onboarding-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={() => handleStepChange(4)}
                  disabled={formData.reasons_for_using.length === 0}
                  className="flex-1 rounded-full px-6 py-5 h-auto text-base font-bold text-white bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8] disabled:opacity-50"
                  data-testid="onboarding-reasons-continue"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Values */}
          {step === 4 && (
            <div className="space-y-6" data-testid="onboarding-values">
              <div className="space-y-2 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold text-white">
                  What matters most to you?
                </h2>
                <p className="text-sm text-[#CBD5E0]">
                  Select all that resonate with you
                </p>
              </div>

              <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] p-6">
                <div className="grid grid-cols-2 gap-3">
                  {valuesOptions.map((value) => {
                    const isSelected = formData.selected_values.includes(value);
                    return (
                      <button
                        key={value}
                        onClick={() => handleValueToggle(value)}
                        className={`p-4 rounded-xl border-2 text-base font-medium transition-all ${
                          isSelected
                            ? 'border-[#06D6A0] bg-[#06D6A0]/20 text-[#06D6A0] ring-2 ring-[#06D6A0]/30'
                            : 'border-[#4A5568] bg-[#3A4550] hover:border-[#06D6A0]/50 text-[#CBD5E0]'
                        }`}
                        data-testid={`value-option-${value}`}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
                {formData.selected_values.length > 0 && (
                  <p className="text-sm text-[#06D6A0] mt-4 text-center font-medium">
                    {formData.selected_values.length} selected
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleStepChange(3)}
                  variant="outline"
                  className="rounded-full px-6 py-5 h-auto text-base font-medium border-[#4A5568] text-[#CBD5E0] hover:bg-[#4A5568]"
                  data-testid="onboarding-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={() => handleStepChange(5)}
                  disabled={formData.selected_values.length === 0}
                  className="flex-1 rounded-full px-6 py-5 h-auto text-base font-bold text-white bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8] disabled:opacity-50"
                  data-testid="onboarding-values-continue"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Intention */}
          {step === 5 && (
            <div className="space-y-6" data-testid="onboarding-intention">
              <div className="space-y-2 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold text-white">
                  Set your intention
                </h2>
                <p className="text-sm text-[#CBD5E0]">
                  Complete this sentence
                </p>
              </div>

              <div className="bg-[#3A4550] rounded-2xl border border-[#4A5568] p-6 space-y-4">
                <Label className="text-lg font-medium text-white">
                  I am becoming...
                </Label>
                <Textarea
                  value={formData.intention_statement}
                  onChange={(e) => setFormData({ ...formData, intention_statement: e.target.value })}
                  placeholder="someone who takes care of their needs"
                  className="min-h-32 rounded-xl border-[#4A5568] bg-[#2D3640] text-white placeholder:text-[#A0AEC0] focus:border-[#00B4D8] focus:ring-[#00B4D8]/20 text-base"
                  data-testid="intention-input"
                />
                <p className="text-xs text-[#CBD5E0]">
                  This is just for you. You can change it anytime.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => handleStepChange(4)}
                  variant="outline"
                  className="rounded-full px-6 py-5 h-auto text-base font-medium border-[#4A5568] text-[#CBD5E0] hover:bg-[#4A5568]"
                  data-testid="onboarding-back-btn"
                >
                  Back
                </Button>
                <Button
                  onClick={handleComplete}
                  disabled={loading || !formData.intention_statement.trim()}
                  className="flex-1 rounded-full px-6 py-5 h-auto text-base font-bold text-white bg-gradient-to-r from-[#00B4D8] to-[#06D6A0] hover:from-[#06D6A0] hover:to-[#00B4D8] disabled:opacity-50"
                  data-testid="onboarding-complete-btn"
                >
                  {loading ? 'Saving...' : 'Complete'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
